import React, { useState, useEffect, useRef } from 'react';

import { Button } from "@progress/kendo-react-buttons";
import { ContextMenu, MenuItem } from "@progress/kendo-react-layout";
import { Input, Checkbox, TextBox, NumericTextBox, MaskedTextBox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { DatePicker, DateTimePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { Resizable } from 'react-resizable';

import QueryViewStyleDialog from 'views/admin/queries/QueryViewStyleDialog.jsx';
import QuerySelectorDialog from 'views/admin/queries/QuerySelectorDialog.jsx';
import QueryCustomIndexDialog from 'views/admin/queries/QueryCustomIndexDialog.jsx';

export default function QueryViewIndividualFieldView(props) {

	const { field, fieldStyle, fieldValue, viewStyle, wrapped,  
			fieldUpdated, fieldRemoved, fieldValueChanged,
			isEditable,
			contextMenuVisible, contextMenuOffset, displayContextMenu,
			onDragStart, onDragOver, onDrop,
			runSearch, workflowId, fromAdmin } = props;

	const [stylePromptVisible, setStylePromptVisible] = useState(false);
	const [selectorDialogVisible, setSelectorDialogVisible] = useState(false);
	const [customIndexDialogVisible, setCustomIndexDialogVisible] = useState(false);
	const [resizing, setResizing] = useState(false);
	const rootRef = useRef(null);
	
	useEffect(() => {
		// handle that the Kendo ComboBox does not honor the setting of the aria-label
		if (field?.type === 'COMBOBOX' && rootRef.current) {
			const comboboxInput = rootRef.current.querySelector('.k-combobox input');
			comboboxInput.setAttribute("aria-label", field.name);
		}
	}, [isEditable]);
	

	/* Event Handlers */
  	const contextMenuClicked = (e) => {
		if (e.itemId == "0") setStylePromptVisible(true);
		else fieldRemoved(field);
	};
  	
 	const styleUpdated = (styles) => {
		field.style = JSON.stringify(styles);
		setStylePromptVisible(false);
		fieldUpdated(field);		
	};

	const valueSelected = (value) => {
		fieldValueChanged(field, value);
		setSelectorDialogVisible(false);
		setCustomIndexDialogVisible(false);
	};
	
	const onResize = (event, {size}) => {
		let newStyle = fieldStyle;
		newStyle.width = size.width;
		newStyle.height = size.height;
    	styleUpdated(newStyle);
		event.preventDefault();
		event.stopPropagation();
	};
	
	const onKeyDown = (event) => {
		// if no value specified, no need to run search
		if (fieldValue === null || fieldValue === undefined || fieldValue == '') return;
		
		// otherwise, check if key should trigger search
		let config = JSON.parse(field.config);
		let keyTriggers = config.keyTriggers ? config.keyTriggers : [{ key: "Enter", shift: false, ctrl: false, alt: false, meta: false }];
		let possibleTriggers = keyTriggers.filter((trigger) => trigger.key == event.key);
		
		// no possible matching keys, so exit
		if (possibleTriggers.length === 0) return;
		
		// else possible matching key trigger, check for key modifier
		if (event.shiftKey && possibleTriggers.filter((trigger) => trigger.shift).length > 0)
			runSearch();
		else if (event.ctrlKey && possibleTriggers.filter((trigger) => trigger.ctrl).length > 0)
			runSearch();
		else if (event.altKey && possibleTriggers.filter((trigger) => trigger.alt).length > 0) 
			runSearch();
		else if (event.metaKey && possibleTriggers.filter((trigger) => trigger.meta).length > 0)
			runSearch();
		else if (!event.shiftKey && !event.ctrlKey && !event.altkey && !event.metaKey 
					&& possibleTriggers.filter((trigger) => !trigger.shift && !trigger.ctrl && !trigger.alt && !trigger.meta).length > 0)
			runSearch();
	};
	

	/* Helper Functions */
	const getWidth = () => {
		if (fieldStyle.width == null || fieldStyle.width == undefined) return 200;
		else return fieldStyle.width; 
	};
	
	const getHeight = () => {
		if (fieldStyle.height == null || fieldStyle.height == undefined) return 30;
		else return fieldStyle.height;
	}
	
	const stripPt = (fontSize) => {
		let ptIdx = fontSize.indexOf("pt");
		return parseInt(fontSize.substring(0, ptIdx));	
	};
	
	const getRangeToWidth = () => {
		let fontSize = stripPt(getCombinedStyle().fontSize);
		return (fontSize * 1.11 + 6) + "px";
	};
	
	const getRangeInputWidth = () => {
		let fontSize = stripPt(getCombinedStyle().fontSize);
		let toWidth = fontSize * 1.11 + 6;
		return "calc(50% - " + (toWidth / 2) + "px)";
	};
	
	const getCombinedStyle = () => {
		let combined = JSON.parse(JSON.stringify(viewStyle));
		for (let key in fieldStyle) combined[key] = fieldStyle[key];
		return combined;
	};
	
	const getDateValue = () => {
		let val = fieldValue;
		if (val) val = new Date(val);
		return val;
	};

	const getFromDateValue = () => {
		let val = fieldValue ? fieldValue.from : fieldValue;
		if (val) val = new Date(val);
		return val;
	};
		
	const getToDateValue = () => {
		let val = fieldValue ? fieldValue.to : fieldValue;
		if (val) val = new Date(val);
		return val;
	};

	const inputWidth = getRangeInputWidth();
	const toWidth = getRangeToWidth();
	
	
	/* Rendering functions */
	const getFieldElem = () => {
		return (
			<div 
				ref={rootRef}
				draggable={isEditable && !resizing}
				key={field.id} 
				className={"constituo-query-view-field"}
				style={{
					display: "inline-block",
					width: (wrapped ? "100%" : undefined)
				}}
				onKeyDown={onKeyDown}
				onContextMenu={(e) => displayContextMenu(e, field)}
				onDragStart={() => onDragStart(field)}			
				onDragOver={() => onDragOver(field)}
				onDragEnd={onDrop}	
			>
				<div style={getInnerStyle()}>
					{ getLabel() }
					{ getResizableElem(getInput()) }
				</div>
			</div>
		);
	};
	
	const getInnerStyle = () => {
		let elemStyles = JSON.parse(JSON.stringify(viewStyle));
		for (let key in fieldStyle) elemStyles[key] = fieldStyle[key];
		delete elemStyles['width'];
		elemStyles.display = 'inline-block';
		return elemStyles;
	};
	
	const getResizableElem = (inputElem) => {
		let divStyle = { display: "inline-block", width: getWidth(), height: getHeight(), lineHeight: getHeight() + "px" };
		if (isEditable) divStyle['position'] = 'relative';
		if (field.type === 'BOOLEAN') {
			divStyle['height'] = '20px';
			delete divStyle.width;
			delete divStyle.lineHeight;
		} else if (field.type.includes('RANGE')) 
			divStyle['width'] = getWidth() * 2;
		
		if (isEditable)
			return (
				<React.Fragment>
					<Resizable 
						width={getWidth()} 
						height={getHeight()}
						axis="both"
						draggableOpts={{
							grid: [5, 5]
						}}
						onResize={(event, props) => onResize(event, props, field)}
						onResizeStart={() => { setResizing(true); }}
						onResizeStop={() => { setResizing(false); }}
					> 
						<div style={divStyle} >
							{ inputElem }
						</div>
					</Resizable>
				</React.Fragment>
			);
		else 
			return (
				<div style={divStyle}>
					{inputElem}
				</div>
			);
	};
	
	const getLabel = () => {
		return (
			<span style={{ position: "relative", top: "2px" }}>{field.name + ": "}</span>	
		);
	};
	
	const getInput = () => {
		let config = JSON.parse(field.config);
		if (field.type == 'STRING') 
			return getStringField(config);
		else if (field.type == 'STRINGRANGE')
			return getStringRangeField(config);
		else if (field.type == 'NUMERIC')
			return getNumericField(config);
		else if (field.type == 'NUMERICRANGE')
			return getNumericRangeField(config);
		else if (field.type == 'DATE')
			return getDateField(config);
		else if (field.type == 'DATERANGE')
			return getDateRangeField(config);
		else if (field.type == 'TIMESTAMP')
			return getTimestampField(config);
		else if (field.type == 'TIMESTAMPRANGE')
			return getTimestampRangeField(config);
		else if (field.type == 'TIME')
			return getTimeField(config);
		else if (field.type == 'TIMERANGE')
			return getTimeRangeField(config);
		else if (field.type == 'BOOLEAN')
			return getBooleanField(config);
		else if (field.type == 'COMBOBOX')
			return getComboBoxField(config);
		else if (field.type == 'SELECTOR')
			return getSelectorField(config);
		else if (field.type == 'CUSTOMINDEX')
			return getCustomIndexField(config);			
		else if (field.type == 'PASSWORD')
			return getPasswordField(config);
		else if (field.type == 'MASKEDEDIT')
			return getMaskedEditField(config);
		else
			return null;
	};
	
	const getStringField = (config) => {
		return (
			<TextBox 
				tabIndex={0}
				aria-label={field.name}
				value={fieldValue == undefined ? "" : fieldValue}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable}
			/>
		);
	};
	
	const getStringRangeField = (config) => {
		return (
			<div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<TextBox 
						tabIndex={0}
						aria-label={field.name + " From"}
						value={(fieldValue ? (fieldValue.from == undefined ? "" : fieldValue.from) : fieldValue)}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, from: e.value})}
						placeholder={config.fromPlaceholder}
						readOnly={isEditable}
					/>
				</div>
				<div style={{display: "inline-block", textAlign: "center", width: toWidth, height: "100%"}}> to </div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<TextBox 
						tabIndex={0}
						aria-label={field.name + " To"}
						value={(fieldValue ? (fieldValue.to == undefined ? "" : fieldValue.to) : fieldValue)}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, to: e.value})}
						placeholder={config.toPlaceholder}
						readOnly={isEditable}
					/>
				</div>
			</div>
		);
	};
	
	const getNumericField = (config) => {
		return (
			<NumericTextBox
				tabIndex={0}
				aria-label={field.name}
				title={field.name}
				value={fieldValue == "" ? null : fieldValue}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable ? true : false}
			/>
		);
	};
	
	const getNumericRangeField = (config) => {
		return (
			<div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<NumericTextBox
						tabIndex={0}
						aria-label={field.name + " From"}
						title={field.name + " From"}
						value={(fieldValue == "" || !fieldValue ? null : (fieldValue.from == "" ? null : fieldValue.from))}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, from: e.value})}
						placeholder={config.fromPlaceholder}
						readOnly={isEditable ? true : false}
					/>
				</div>
				<div style={{display: "inline-block", textAlign: "center", width: toWidth, height: "100%"}}> to </div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<NumericTextBox 
						tabIndex={0}
						aria-label={field.name + " To"}
						title={field.name + " To"}
						value={(fieldValue == "" || !fieldValue ? null : (fieldValue.to == "" ? null : fieldValue.to))}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, to: e.value})}
						placeholder={config.toPlaceholder}
						readOnly={isEditable ? true : false}
					/>
				</div>
			</div>
		);
	};
	
	const getDateField = (config) => {
		return (
			<DatePicker
				tabIndex={0}
				ariaLabelledBy={field.name}
				title={field.name}
				value={getDateValue()}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				format={field.format}
				readOnly={isEditable}
			/>	
		);
	};
	
	const getTimestampField = (config) => {
		return (
			<DateTimePicker
				tabIndex={0}
				ariaLabelledBy={field.name}
				title={field.name}
				value={getDateValue()}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeHolder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				format={field.format}
				readOnly={isEditable}
			/>
		);
	};
	
	const getDateRangeField = (config) => {
		return (
			<div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<DatePicker
						tabIndex={0}
						ariaLabelledBy={field.name + " From"}
						title={field.name + " From"}
						value={getFromDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, from: e.value})}
						placeholder={config.fromPlaceholder}
						format={field.format}
						readOnly={isEditable}
					/>
				</div>
				<div style={{display: "inline-block", textAlign: "center", width: toWidth, height: "100%"}}> to </div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<DatePicker 
						tabIndex={0}
						ariaLabelledBy={field.name + " To"}
						title={field.name + " To"}
						value={getToDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, to: e.value})}
						placeholder={config.toPlaceholder}
						format={field.format}
						readOnly={isEditable}
					/>
				</div>
			</div>
		);
	};
	
	const getTimestampRangeField = (config) => {
		return (
			<div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<DateTimePicker
						tabIndex={0}
						ariaLabelledBy={field.name + " From"}
						title={field.name + " From"}
						value={getFromDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, from: e.value})}
						placeholder={config.fromPlaceholder}
						format={field.format}
						readOnly={isEditable}
					/>
				</div>
				<div style={{display: "inline-block", textAlign: "center", width: toWidth, height: "100%"}}> to </div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<DateTimePicker 
						tabIndex={0}
						ariaLabelledBy={field.name + " To"}
						title={field.name + " To"}
						value={getToDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, to: e.value})}
						placeholder={config.toPlaceholder}
						format={field.format}
						readOnly={isEditable}
					/>
				</div>
			</div>
		);
	};
	
	const getTimeField = (config) => {
		return (
			<TimePicker
				tabIndex={0}
				ariaLabelledBy={field.name}
				title={field.name}
				value={getDateValue()}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable}
			/>
		);
	}
	
	const getTimeRangeField = (config) => {
		return (
			<div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<TimePicker
						tabIndex={0}
						ariaLabelledBy={field.name + " From"}
						title={field.name + " From"}
						value={getFromDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, from: e.value})}
						placeholder={config.fromPlaceholder}
						readOnly={isEditable}
					/>
				</div>
				<div style={{display: "inline-block", textAlign: "center", width: toWidth, height: "100%"}}> to </div>
				<div style={{display: "inline-block", width: inputWidth, height: "100%"}}>
					<TimePicker 
						tabIndex={0}
						ariaLabelledBy={field.name + " To"}
						title={field.name + " To"}
						value={getToDateValue()}
						onChange={(e) => fieldValueChanged(field, {...fieldValue, to: e.value})}
						placeholder={config.toPlaceholder}
						readOnly={isEditable}
					/>
				</div>
			</div>
		);
	};
	
	const getBooleanField = () => {
		return (
			<Checkbox
				tabIndex={0}
				aria-label={field.name}
				title={field.name}
				value={fieldValue}
				onChange={(e) => fieldValueChanged(field, e.value)}
				readOnly={isEditable}
			/>
		);
	};
	
	const getComboBoxField = (config) => {
		let choices = config.choices ? config.choices.split('\n') : [];
		return (
			<ComboBox
				aria-label={field.name}
				title={field.name}
				value={fieldValue}
				data={choices}
				clearButton={false}
				filterable={false}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable}
			/>
		);
	};
	
	const getSelectorField = (config) => {
		return (
			<React.Fragment>
				<TextBox 
					tabIndex={0}
					aria-label={field.name}
					title={field.name}
					value={fieldValue == undefined ? "" : fieldValue}
					onChange={(e) => fieldValueChanged(field, e.value)} 
					placeholder={config.placeholder}
					readOnly={true}
					style={{width: "calc(100% - 30px", height: "100%"}}
				/>
				<Button 
					tabIndex={0}
					aria-label={field.name}
					title={field.name}
					role="button"
					iconClass="fa fa-fw fa-small fa-search"
					onClick={() => setSelectorDialogVisible(true)}
				/>
			</React.Fragment>
		);
	};
	
	const getCustomIndexField = (config) => {
		return (
			<>
				<TextBox
					tabIndex={0}
					aria-label={field.name}
					title={field.name}
					value={fieldValue == undefined ? "" : fieldValue}
					onChange={(e) => fieldValueChanged(field, e.value)}
					placeholder={config.placeholder}
					readOnly={false}
					style={{width: "calc(100% - 30px", height: "100%"}}
				/>
				<Button 
					tabIndex={0}
					aria-label={field.name}
					title={field.name}
					role="button"
					iconClass="fa fa-fw fa-small fa-search"
					onClick={() => setCustomIndexDialogVisible(true)}
				/>					
			</>
		);
	}
	
	const getPasswordField = (config) => {
		return (
			<Input 
				tabIndex={0}
				aria-label={field.name}
				title={field.name}
				value={fieldValue}
				type={"password"}
				onChange={(e) => fieldValueChanged(field, e.value)} 
				placeholder={config.placeholder}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable}
			/>
		);
	};
	
	const getMaskedEditField = (config) => {
		return (
			<MaskedTextBox 
				tabIndex={0}
				aria-label={field.name}
				title={field.name}
				value={fieldValue}
				onChange={(e) => fieldValueChanged(field, e.value)}
				placeholder={config.placeholder}
				mask={config.mask}
				style={{width: "100%", height: "100%"}}
				readOnly={isEditable}
			/>
		);
	};

	/* Commented out for now, could be brought back to make 
	   floating buttons to open up the style dialog and
	   remove the field */
	/*
	let buttons = [
		(
			<FloatingActionButton
				key="style-btn" title="Edit Style"
				iconClass="fa fa-fw fa-small fa-brush"
				themeColor={null}
				style={{ fontSize: "8pt", zIndex: "80" }}
				size={"small"}
				tabIndex={0}
				align={{ horizontal: "end", vertical: "top" }}
				alignOffset={{ y: 0, x: 35 }}
				positionMode="absolute"
				rounded="full"
				onClick={() => setStylePromptVisible(true)}
			/>	
		),
		(
			<FloatingActionButton
				key="delete-btn" title="Remove"
				iconClass="fa fa-fw fa-small fa-trash"
				themeColor={null}
				style={{ fontSize: "8pt", zIndex: "80" }}
				size={"small"}
				tabIndex={0}
				align={{ horizontal: "end", vertical: "top" }}
				alignOffset={{ y: 0, x: 20 }}
				positionMode="absolute"
				rounded="full"
				onClick={() => fieldRemoved(field)}
			/>	
		)
	];
	*/
	
	if (field && field.searchable && !field.deleted)
		return (	
			<React.Fragment>
				{ getFieldElem() }
			
				{ stylePromptVisible && (
            	<QueryViewStyleDialog
            		initStyles={getCombinedStyle()}
            		includeFieldStyleInputs={true}
            		okCallback={styleUpdated}
            		cancelCallback={() => setStylePromptVisible(false)}
            	/>
	         	) }
	         	{ selectorDialogVisible && (
				<QuerySelectorDialog
					field={field}
					procedure={JSON.parse(field.config).script}
					okCallback={valueSelected}
					cancelCallback={() => setSelectorDialogVisible(false)}
					workflowId={workflowId}
				/> 
				) }
	         	{ customIndexDialogVisible && (
				<QueryCustomIndexDialog
					field={field}
					okCallback={valueSelected}
					cancelCallback={() => setCustomIndexDialogVisible(false)}
					workflowId={workflowId}
					fromAdmin={fromAdmin}
				/> 
				) }				
	         	{ isEditable && contextMenuVisible && (
					<ContextMenu 
						show={true}
						offset={contextMenuOffset}
						onSelect={contextMenuClicked}
     				>
          				<MenuItem text="Edit Style" />
            			<MenuItem text="Remove" />
      				</ContextMenu>
				) }	
			</React.Fragment>
		);
	
	else
		return null;

}